import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../supabaseClient";
import { useUserRole } from "../UserRoleContext";
import { collectNestedKeys, getNestedValue } from "../utils/gtm";

export default function GTMTagReferenceAdmin() {
  const role = useUserRole();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [newFunction, setNewFunction] = useState("");
  const [newFields, setNewFields] = useState("[]");
  const [missing, setMissing] = useState({});
  const [configs, setConfigs] = useState({});

  const fetchRows = useCallback(async () => {
    const { data } = await supabase
      .from("gtm_tag_reference")
      .select("id,function,fields,ignore")
      .order("created_at", { ascending: true });
    if (data) {
      setRows(
        data.map((r) => ({
          ...r,
          fieldsStr: JSON.stringify(r.fields),
          ignore: r.ignore,
        }))
      );
      fetchMissing(data.map((r) => r.function));
    }
  }, []);

  const fetchMissing = async (known = []) => {
    const { data } = await supabase.from("gtm_container_contents").select("tags");
    const funcs = {};
    if (data) {
      data.forEach((row) => {
        if (Array.isArray(row.tags)) {
          row.tags.forEach((tag) => {
            const fn = tag.function;
            if (fn && !known.includes(fn)) {
              if (!funcs[fn]) funcs[fn] = [];
              funcs[fn].push(tag);
            }
          });
        }
      });
    }
    setMissing(funcs);
    const cfg = {};
    Object.entries(funcs).forEach(([fn, tags]) => {
      const keys = Array.from(new Set(tags.flatMap((t) => collectNestedKeys(t))));
      const keyMap = {};
      keys.forEach((k) => {
        keyMap[k] = { selected: false, mapTo: k, passthrough: true, value: "", definition: "" };
      });
      cfg[fn] = { keys: keyMap };
    });
    setConfigs(cfg);
  };

  useEffect(() => {
    if (role === "admin") fetchRows();
  }, [role, fetchRows]);

  const updateRow = async (row) => {
    let parsed;
    try {
      parsed = JSON.parse(row.fieldsStr);
      if (!Array.isArray(parsed)) throw new Error();
    } catch {
      alert("Fields must be a JSON array");
      return;
    }
    const { error } = await supabase
      .from("gtm_tag_reference")
      .update({ fields: parsed })
      .eq("id", row.id);
    if (!error) fetchRows();
  };

  const toggleIgnore = async (row) => {
    const { error } = await supabase
      .from("gtm_tag_reference")
      .update({ ignore: !row.ignore })
      .eq("id", row.id);
    if (!error) fetchRows();
  };

  const addRow = async () => {
    if (!newFunction.trim()) return;
    if (rows.some((r) => r.function === newFunction.trim())) {
      alert("Function already exists");
      return;
    }
    let parsed;
    try {
      parsed = JSON.parse(newFields);
      if (!Array.isArray(parsed)) throw new Error();
    } catch {
      alert("Fields must be a JSON array");
      return;
    }
    const { error } = await supabase
      .from("gtm_tag_reference")
      .insert({ function: newFunction.trim(), fields: parsed, ignore: false });
    if (!error) {
      setNewFunction("");
      setNewFields("[]");
      fetchRows();
    }
  };

  const deleteRow = async (id) => {
    const { error } = await supabase.from("gtm_tag_reference").delete().eq("id", id);
    if (!error) fetchRows();
  };

  if (role !== "admin") {
    return <div className="page-container">Access denied.</div>;
  }

  const TagTable = ({ tags }) => {
    if (!tags || tags.length === 0) return null;
    const keys = Array.from(new Set(tags.flatMap((t) => collectNestedKeys(t))));
    return (
      <table className="ui-table"
        border="1"
        cellPadding="4"
        style={{ borderCollapse: "collapse", width: "100%" }}
      >
        <thead>
          <tr>{keys.map((k) => <th key={k}>{k}</th>)}</tr>
        </thead>
        <tbody>
          {tags.map((tag, idx) => (
            <tr key={idx}>
              {keys.map((k) => (
                <td key={k}>{String(getNestedValue(tag, k) ?? "")}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  const updateConfig = (fn, key, field, value) => {
    setConfigs((prev) => ({
      ...prev,
      [fn]: {
        keys: {
          ...prev[fn].keys,
          [key]: { ...prev[fn].keys[key], [field]: value },
        },
      },
    }));
  };

  const addMappedFunction = async (fn) => {
    const cfg = configs[fn];
    if (!cfg) return;
    const fields = Object.entries(cfg.keys)
      .filter(([, c]) => c.selected)
      .map(([k, c]) => ({
        key: k,
        mapTo: c.mapTo || k,
        passthrough: c.passthrough,
        value: c.passthrough ? undefined : c.value,
        definition: c.definition,
      }));
    if (fields.length === 0) {
      alert("Select at least one key");
      return;
    }
    const { error } = await supabase
      .from("gtm_tag_reference")
      .insert({ function: fn, fields, ignore: false });
    if (!error) {
      fetchRows();
      setMissing((prev) => {
        const m = { ...prev };
        delete m[fn];
        return m;
      });
      setConfigs((prev) => {
        const p = { ...prev };
        delete p[fn];
        return p;
      });
    }
  };

  const ignoreFunction = async (fn) => {
    const { error } = await supabase
      .from("gtm_tag_reference")
      .insert({ function: fn, fields: [], ignore: true });
    if (!error) {
      fetchRows();
      setMissing((prev) => {
        const m = { ...prev };
        delete m[fn];
        return m;
      });
      setConfigs((prev) => {
        const p = { ...prev };
        delete p[fn];
        return p;
      });
    }
  };

  return (
    <div className="page-container">
      <h2>Edit GTM Tag References</h2>
      <button onClick={() => navigate("/admin")}>Back</button>
      <table className="ui-table" style={{ marginTop: "1rem" }}>
        <thead>
          <tr>
            <th>Function</th>
            <th>Fields (JSON array)</th>
            <th>Ignored</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.id}>
              <td>{row.function}</td>
              <td>
                <input
                  type="text"
                  style={{ width: "100%" }}
                  value={row.fieldsStr}
                  onChange={(e) =>
                    setRows((prev) =>
                      prev.map((r) =>
                        r.id === row.id ? { ...r, fieldsStr: e.target.value } : r
                      )
                    )
                  }
                />
              </td>
              <td
                style={{ textAlign: "center", cursor: "pointer" }}
                onClick={() => toggleIgnore(row)}
              >
                <input
                  type="checkbox"
                  checked={row.ignore}
                  onChange={() => toggleIgnore(row)}
                  onClick={(e) => e.stopPropagation()}
                  aria-label={`Toggle ignore for ${row.function}`}
                />
              </td>
              <td>
                <button onClick={() => updateRow(row)}>Save</button>
                <button
                  onClick={() => deleteRow(row.id)}
                  style={{ marginLeft: "0.5rem" }}
                >
                  Delete
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <h3 style={{ marginTop: "1rem" }}>Add New Row</h3>
      <div>
        <input
          type="text"
          placeholder="function"
          value={newFunction}
          onChange={(e) => setNewFunction(e.target.value)}
        />
        <input
          type="text"
          placeholder='["function"]'
          style={{ marginLeft: "0.5rem", width: "60%" }}
          value={newFields}
          onChange={(e) => setNewFields(e.target.value)}
        />
        <button onClick={addRow} style={{ marginLeft: "0.5rem" }}>
          Add
        </button>
      </div>
      <p style={{ marginTop: "1rem" }}>
        Specify the <code>function</code> exactly as it appears in your GTM tag
        export. The <code>fields</code> column accepts a JSON array of tag
        object keys to keep, for example
        <code>["function", "html", "firingTriggerId"]</code>. The website
        scraper uses these arrays to remove any other properties.
      </p>
      <div style={{ marginTop: "1rem" }}>
        <h3>Functions Needing Mapping</h3>
        {Object.keys(missing).length === 0 && (
          <p>All functions are mapped.</p>
        )}
        {Object.entries(missing).map(([fn, tags]) => {
          const cfg = configs[fn];
          if (!cfg) return null;
          const keyEntries = Object.entries(cfg.keys);
          return (
            <div key={fn} style={{ marginTop: "1rem" }}>
              <h4>{fn}</h4>
              <table className="ui-table"
                border="1"
                cellPadding="4"
                style={{ borderCollapse: "collapse", width: "100%" }}
              >
                <thead>
                  <tr>
                    <th>Keep</th>
                    <th>Key</th>
                    <th>Map To</th>
                    <th>Pass Through</th>
                    <th>Value</th>
                    <th>Definition</th>
                  </tr>
                </thead>
                <tbody>
                  {keyEntries.map(([k, v]) => (
                    <tr key={k}>
                      <td
                        onClick={() =>
                          updateConfig(fn, k, "selected", !v.selected)
                        }
                        style={{ cursor: "pointer" }}
                      >
                        <input
                          type="checkbox"
                          checked={v.selected}
                          onChange={(e) =>
                            updateConfig(fn, k, "selected", e.target.checked)
                          }
                          onClick={(e) => e.stopPropagation()}
                          aria-label={`Keep ${k}`}
                        />
                      </td>
                      <td>{k}</td>
                      <td>
                        <input
                          type="text"
                          value={v.mapTo}
                          onChange={(e) => updateConfig(fn, k, "mapTo", e.target.value)}
                        />
                      </td>
                      <td>
                        <select
                          value={v.passthrough ? "pass" : "set"}
                          onChange={(e) =>
                            updateConfig(fn, k, "passthrough", e.target.value === "pass")
                          }
                        >
                          <option value="pass">Pass</option>
                          <option value="set">Set</option>
                        </select>
                      </td>
                      <td>
                        <input
                          type="text"
                          value={v.value}
                          disabled={v.passthrough}
                          onChange={(e) => updateConfig(fn, k, "value", e.target.value)}
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          value={v.definition}
                          onChange={(e) =>
                            updateConfig(fn, k, "definition", e.target.value)
                          }
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <button onClick={() => addMappedFunction(fn)} style={{ marginTop: "0.5rem" }}>
                Add Function
              </button>
              <button onClick={() => ignoreFunction(fn)} style={{ marginTop: "0.5rem", marginLeft: "0.5rem" }}>
                Ignore
              </button>
              <TagTable tags={tags} />
            </div>
          );
        })}
      </div>
    </div>
  );
}
